import React, { useState } from "react";
import { toast } from "react-toastify";
import LoadingAnimation from "../Components/Loading/LoadingAnimation";

const ConfirmationDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  isLoading,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
        <h3 className="text-lg font-semibold mb-2">
          {title || "Are you sure?"}
        </h3>

        <p className="text-gray-600 mb-4">
          {message || "This action cannot be undone."}
        </p>

        {isLoading && (
          <div className="flex justify-center items-center mb-4">
            <LoadingAnimation />
          </div>
        )}

        <div className="flex justify-end space-x-3 mt-4">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 border bg-[#8B4513] text-white rounded-md"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={`px-4 py-2 flex items-center justify-center rounded-md text-white bg-[#8B4513]`}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export const useConfirmation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Store config outside of async onConfirm logic
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [onConfirmCallback, setOnConfirmCallback] = useState(() => () => {});

  const showConfirmation = (
    newTitle,
    newMessage,
    onConfirm,
    suppressToast = false
  ) => {
    setTitle(newTitle);
    setMessage(newMessage);
    setIsOpen(true);

    setOnConfirmCallback(() => async () => {
      setIsLoading(true);
      try {
        await onConfirm();
        if (!suppressToast) {
          toast.success("Operation completed successfully");
        }
      } catch (error) {
        if (!suppressToast) {
          toast.error(error.message || "Operation failed");
        }
      } finally {
        setIsLoading(false);
        setIsOpen(false);
      }
    });
  };

  const Dialog = () => (
    <ConfirmationDialog
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      onConfirm={onConfirmCallback}
      title={title}
      message={message}
      isLoading={isLoading}
    />
  );

  return { showConfirmation, ConfirmationDialog: Dialog };
};

export default ConfirmationDialog;
